// ─────────────────────────────────────────────
// BOSS_PASSAGES.js
// Coordinator for boss fight reading passages.
//
// Imports all book arrays and exports a single
// getBossPassage(grade, position) function.
//
// Adding a new book later: import it here,
// add it to GRADE_CONFIG, done. SightWordQuiz.js
// never needs to change.
// ─────────────────────────────────────────────

import ALICE from "./ALICE";
import TREASURE_ISLAND from "./TREASURE_ISLAND";
import TALE_OF_TWO_CITIES from "./TALE_OF_TWO_CITIES";

// ── How many paragraphs to show per grade ─────
// K      = 1 sentence  (handled by SENTENCE_ONLY flag)
// 1st    = 1 paragraph
// 2nd    = 2 paragraphs
// 3rd    = 3 paragraphs
// 4th    = 4 paragraphs
// 5th+   = 5 paragraphs
const GRADE_CONFIG = {
  "K":    { book: ALICE,              title: "Alice's Adventures in Wonderland", paragraphs: 1, sentenceOnly: true  },
  "1st":  { book: ALICE,              title: "Alice's Adventures in Wonderland", paragraphs: 1, sentenceOnly: false },
  "2nd":  { book: ALICE,              title: "Alice's Adventures in Wonderland", paragraphs: 2, sentenceOnly: false },
  "3rd":  { book: ALICE,              title: "Alice's Adventures in Wonderland", paragraphs: 3, sentenceOnly: false },
  "4th":  { book: ALICE,              title: "Alice's Adventures in Wonderland", paragraphs: 4, sentenceOnly: false },
  "5th":  { book: ALICE,              title: "Alice's Adventures in Wonderland", paragraphs: 5, sentenceOnly: false },
  "6th":  { book: TREASURE_ISLAND,    title: "Treasure Island",                  paragraphs: 5, sentenceOnly: false },
  "7th":  { book: TREASURE_ISLAND,    title: "Treasure Island",                  paragraphs: 5, sentenceOnly: false },
  "8th":  { book: TALE_OF_TWO_CITIES, title: "A Tale of Two Cities",             paragraphs: 5, sentenceOnly: false },
  "9th":  { book: TALE_OF_TWO_CITIES, title: "A Tale of Two Cities",             paragraphs: 5, sentenceOnly: false },
  "10th": { book: TALE_OF_TWO_CITIES, title: "A Tale of Two Cities",             paragraphs: 5, sentenceOnly: false },
  "11th": { book: TALE_OF_TWO_CITIES, title: "A Tale of Two Cities",             paragraphs: 5, sentenceOnly: false },
  "12th": { book: TALE_OF_TWO_CITIES, title: "A Tale of Two Cities",             paragraphs: 5, sentenceOnly: false },
};

// ─────────────────────────────────────────────
// getBossPassage
//
// grade       — e.g. "6th"
// position    — index of the paragraph to start
//               at (saved in Firestore per child)
//
// Returns:
//   text        — the passage string to display
//   newPosition — the next position to save
//   bookTitle   — displayed under the passage
// ─────────────────────────────────────────────
export function getBossPassage(grade, position = 0) {
  const config = GRADE_CONFIG[grade] || GRADE_CONFIG["1st"];
  const { book, title, paragraphs, sentenceOnly } = config;

  // Clamp position to valid range
  const startPos = Math.min(position, book.length - 1);

  let text;
  let newPosition;

  if (sentenceOnly) {
    // K grade: pull just the first sentence from the paragraph
    const paragraph = book[startPos] || "";
    const firstSentence = paragraph.split(/(?<=[.!?])\s+/)[0] || paragraph;
    text = firstSentence;
    newPosition = startPos + 1 >= book.length ? 0 : startPos + 1;
  } else {
    // All other grades: pull the right number of paragraphs
    const slice = [];
    for (let i = 0; i < paragraphs; i++) {
      const idx = startPos + i;
      if (idx < book.length) {
        slice.push(book[idx]);
      }
    }
    text = slice.join("\n\n");
    const nextPos = startPos + paragraphs;
    // If we've reached the end of the book, wrap back to 0
    newPosition = nextPos >= book.length ? 0 : nextPos;
  }

  return {
    text,
    newPosition,
    bookTitle: title,
  };
}